import React, { useState } from 'react';
import {
  CreditCard, Clock, AlertCircle, Mail, Phone,
  Check, Calendar, FileText, Send, Download
} from 'lucide-react';
import {
  factures, inscriptions, paiements, relances,
  getInscription, getPaiementsByFacture, formatMontant, formatDate
} from '../data/mockData';

const Paiements = () => {
  const [activeTab, setActiveTab] = useState('en-attente');

  const facturesEnAttente = factures.filter(f => f.statut !== 'payee');

  const getMontantPaye = (facture) => {
    return getPaiementsByFacture(facture.id).reduce((sum, p) => sum + p.montant, 0);
  };

  const getProgressPercent = (facture) => {
    const totalPaye = getMontantPaye(facture);
    return facture.totalNet > 0 ? Math.round((totalPaye / facture.totalNet) * 100) : 0;
  };

  const renderEnAttente = () => (
    <div className="paiements-liste">
      {facturesEnAttente.length === 0 && (
        <div className="empty-state">
          <Check size={48} />
          <h3>Tout est à jour</h3>
          <p>Aucune facture en attente de paiement</p>
        </div>
      )}
      {facturesEnAttente.map(facture => {
        const ins = getInscription(facture.inscriptionId);
        const paiementsFacture = getPaiementsByFacture(facture.id);
        const montantPaye = getMontantPaye(facture);
        const reste = facture.totalNet - montantPaye;
        const progress = getProgressPercent(facture);
        const isImpayee = facture.statut === 'impayee';

        return (
          <div
            key={facture.id}
            className={`paiement-card ${facture.statut === 'partielle' ? 'paiement-partiel' : ''} ${isImpayee ? 'paiement-attente' : ''}`}
          >
            <div className="paiement-card-header">
              <div className="paiement-famille">
                <div className="famille-avatar-mini" style={{ background: ins?.color || '#8B5CF6' }}>
                  {ins?.initials || '?'}
                </div>
                <div>
                  <h3>{ins ? `${ins.eleve.prenom} ${ins.eleve.nom}` : 'Inconnu'}</h3>
                  <span className="facture-ref">{facture.numero}</span>
                </div>
              </div>
              {isImpayee && (
                <span className="retard-badge">
                  <AlertCircle size={14} />
                  En retard
                </span>
              )}
            </div>

            <div className="paiement-card-body">
              <div className="paiement-info-grid">
                <div className="paiement-info">
                  <span className="label">Montant total</span>
                  <span className="value montant">{formatMontant(facture.totalNet)}</span>
                </div>
                <div className="paiement-info">
                  <span className="label">Payé</span>
                  <span className="value" style={{ color: 'var(--success)' }}>{formatMontant(montantPaye)}</span>
                </div>
                <div className="paiement-info">
                  <span className="label">Reste</span>
                  <span className="value" style={{ color: 'var(--danger)' }}>{formatMontant(reste)}</span>
                </div>
              </div>

              <div className="progress-section">
                <div className="progress-header">
                  <span>Progression</span>
                  <span>{progress}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <div className="progress-amounts">
                  <span>{formatMontant(montantPaye)} versé</span>
                  <span>{formatMontant(reste)} restant</span>
                </div>
              </div>

              <div className="versements-timeline">
                <h4>Échéancier</h4>
                {paiementsFacture.map(p => (
                  <div key={p.id} className="versement-timeline-item">
                    <div className="versement-dot" />
                    <div className="versement-content">
                      <span>{formatDate(p.date)}</span>
                      <strong>{formatMontant(p.montant)}</strong>
                      <span style={{ color: 'var(--gray-500)' }}>{p.mode}</span>
                    </div>
                  </div>
                ))}
                {reste > 0 && (
                  <div className="versement-timeline-item pending">
                    <div className="versement-dot" />
                    <div className="versement-content">
                      <span>À venir</span>
                      <strong>{formatMontant(reste)}</strong>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="paiement-card-footer">
              <button className="btn btn-secondary btn-sm">
                <Send size={14} /> Relancer
              </button>
              <button className="btn btn-primary btn-sm">
                <CreditCard size={14} /> Enregistrer paiement
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderHistorique = () => (
    <div className="paiements-table-container">
      <table className="paiements-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Élève</th>
            <th>Facture</th>
            <th>Montant</th>
            <th>Mode</th>
            <th>Référence</th>
          </tr>
        </thead>
        <tbody>
          {[...paiements].sort((a, b) => new Date(b.date) - new Date(a.date)).map(paiement => {
            const facture = factures.find(f => f.id === paiement.factureId);
            const ins = facture ? getInscription(facture.inscriptionId) : null;
            return (
              <tr key={paiement.id}>
                <td>{formatDate(paiement.date)}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div className="famille-avatar-mini" style={{ background: ins?.color || '#8B5CF6' }}>
                      {ins?.initials || '?'}
                    </div>
                    {ins ? `${ins.eleve.prenom} ${ins.eleve.nom}` : '—'}
                  </div>
                </td>
                <td><span className="facture-numero-cell">{facture ? facture.numero : '—'}</span></td>
                <td style={{ fontWeight: 600 }}>{formatMontant(paiement.montant)}</td>
                <td><span className="mode-badge">{paiement.mode}</span></td>
                <td style={{ color: 'var(--gray-500)', fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>{paiement.reference || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderRelances = () => (
    <div className="relances-liste">
      <div className="relances-header">
        <h3 style={{ margin: 0 }}>{relances.length} relance{relances.length > 1 ? 's' : ''} envoyée{relances.length > 1 ? 's' : ''}</h3>
      </div>
      <div className="relances-timeline">
        {relances.map(relance => {
          const facture = factures.find(f => f.id === relance.factureId);
          const ins = facture ? getInscription(facture.inscriptionId) : null;
          const iconMap = {
            email: <Mail size={18} />,
            telephone: <Phone size={18} />,
            courrier: <FileText size={18} />
          };

          return (
            <div key={relance.id} className="relance-item">
              <div className="relance-icon">
                {iconMap[relance.type] || <Mail size={18} />}
              </div>
              <div className="relance-content">
                <div className="relance-header">
                  <strong>
                    {ins ? `${ins.eleve.prenom} ${ins.eleve.nom}` : 'Élève'} — {facture?.numero}
                  </strong>
                  <span className="badge badge-warning">{relance.statut}</span>
                </div>
                <p className="relance-message">{relance.message}</p>
                <div className="relance-meta">
                  <span><Calendar size={12} /> {formatDate(relance.date)}</span>
                  <span>Type : {relance.type}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {relances.length === 0 && (
        <div className="empty-state">
          <Mail size={48} />
          <h3>Aucune relance</h3>
          <p>Aucune relance n'a été envoyée</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><CreditCard size={28} /> Paiements & Relances</h1>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary">
            <CreditCard size={16} /> Enregistrer un paiement
          </button>
          <button className="btn btn-secondary">
            <Download size={16} /> Exporter
          </button>
        </div>
      </div>

      <div className="paiements-tabs">
        {[
          { id: 'en-attente', label: 'En attente', count: facturesEnAttente.length },
          { id: 'historique', label: 'Historique', count: paiements.length },
          { id: 'relances', label: 'Relances', count: relances.length },
        ].map(tab => (
          <button
            key={tab.id}
            className={`paiement-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            <span className="tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {activeTab === 'en-attente' && renderEnAttente()}
      {activeTab === 'historique' && renderHistorique()}
      {activeTab === 'relances' && renderRelances()}
    </div>
  );
};

export default Paiements;
